import { useContext } from 'react'

import {
  AuthedAxiosRequests,
  GeckoAuthContext,
  UserId,
} from './geckoAuthContext'
import { GeckoAuthProvider } from './GeckoAuthProvider'
import {
  getOrgMemberPermission,
  getSiteEditPermission,
  getSiteMemberPermission,
  PERMISSION_CANTILEVER_ACCESS_ADMIN,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_VIEWER,
  PERMISSION_CANTILEVER_COMPONENT_ARTIFACTS_EDIT,
  PERMISSION_CANTILEVER_CORE_ADMIN,
  PERMISSION_CANTILEVER_CORE_EDIT,
  PERMISSION_CANTILEVER_EXPLOERER_EDITOR,
  PERMISSION_CANTILEVER_EXPLOERER_VIEWER,
  PERMISSION_CANTILEVER_EXTENSION_CONFIG_EDITOR,
  PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR,
  PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_EDITOR,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_VIEWER,
  PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_EDITOR,
  PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_VIEWER,
  PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_ADMIN,
  PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_EDITOR,
  PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_VIEWER,
  PERMISSION_CANTILEVER_MARKUP_LAYER_EDIT,
  PERMISSION_CANTILEVER_PARTS_THREAD_EDITOR,
  PERMISSION_CANTILEVER_PARTS_THREAD_VIEWER,
  PERMISSION_CANTILEVER_PHOTOGRAMMETRY_ADMIN,
  PERMISSION_CANTILEVER_PHOTOGRAMMETRY_EDITOR,
  PERMISSION_CANTILEVER_PUBLISHER_ARCHIVE_EDITOR,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_GECKO_READ,
  RolodexPermissions,
} from './permissions/rolodexPermissionsList'
import {
  MissingPermissionsPageComponent,
  PermissionsGate,
  PermissionsGateProps,
} from './PermissionsGate'
import { useEasterEggs } from './useEasterEggs'
import { isGovEnvironment } from './vendors/KeycloakProvider'
import { isRunningOnSecondFront } from './vendors/SecondFrontAuthContext'

/**
 * Checks if the user is a domestic commercial user.
 * Domestic commercial users are those who are NOT in a government environment
 * and NOT running on SecondFront (UAE).
 * @param ENVIRONMENT - The environment from useRuntimeVariables()
 * @param RUNNING_ON - The running environment from useRuntimeVariables()
 */
export function isDomesticCommercialUser(
  ENVIRONMENT: string,
  RUNNING_ON: string
): boolean {
  return !isGovEnvironment(ENVIRONMENT) && !isRunningOnSecondFront(RUNNING_ON)
}

export {
  type AuthedAxiosRequests,
  GeckoAuthProvider,
  getOrgMemberPermission,
  getSiteEditPermission,
  getSiteMemberPermission,
  isGovEnvironment,
  isRunningOnSecondFront,
  MissingPermissionsPageComponent,
  PERMISSION_CANTILEVER_ACCESS_ADMIN,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_VIEWER,
  PERMISSION_CANTILEVER_COMPONENT_ARTIFACTS_EDIT,
  PERMISSION_CANTILEVER_CORE_ADMIN,
  PERMISSION_CANTILEVER_CORE_EDIT,
  PERMISSION_CANTILEVER_EXPLOERER_EDITOR,
  PERMISSION_CANTILEVER_EXPLOERER_VIEWER,
  PERMISSION_CANTILEVER_EXTENSION_CONFIG_EDITOR,
  PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR,
  PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_EDITOR,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_VIEWER,
  PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_EDITOR,
  PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_VIEWER,
  PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_ADMIN,
  PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_EDITOR,
  PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_VIEWER,
  PERMISSION_CANTILEVER_MARKUP_LAYER_EDIT,
  PERMISSION_CANTILEVER_PARTS_THREAD_EDITOR,
  PERMISSION_CANTILEVER_PARTS_THREAD_VIEWER,
  PERMISSION_CANTILEVER_PHOTOGRAMMETRY_ADMIN,
  PERMISSION_CANTILEVER_PHOTOGRAMMETRY_EDITOR,
  PERMISSION_CANTILEVER_PUBLISHER_ARCHIVE_EDITOR,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_GECKO_READ,
  PermissionsGate,
  type PermissionsGateProps,
  type RolodexPermissions,
  useEasterEggs,
}

export const useGeckoAuth = () => {
  const context = useContext(GeckoAuthContext)
  if (!context)
    throw new Error('useGeckoAuth() must be used within GeckoAuthProvider')
  return context
}

export {
  AssetOverviewAdminGate,
  AssetOverviewEditorGate,
} from './gates/AssetOverviewGates'
export { CoreAdminGate } from './gates/CoreGates'
export {
  InspectionOverviewAdminGate,
  InspectionOverviewEditorGate,
  InspectionOverviewViewerGate,
} from './gates/InspectionOverviewGates'
export {
  InventoryOverviewAdminGate,
  InventoryOverviewEditorGate,
  InventoryOverviewViewerGate,
} from './gates/InventoryOverviewGates'
export {
  ProcessFlowAdminGate,
  ProcessFlowEditorGate,
  ProcessFlowViewerGate,
} from './gates/ProcessFlowDiagramGates'

export type { UserId }
